import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ShoppingCart, ArrowRight, MapPin, Globe,
  X, Star, Package, ChevronRight, ChevronDown, ChevronLeft, LayoutGrid,
  Check, Plus, Minus, Leaf, Snowflake, Thermometer, Sun, Truck, Calendar, Tag,
  AlertTriangle, Menu, Store as StoreIcon,
} from "lucide-react";

import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/productSlice";
import { addToCart, decrementCartItem } from "../../../redux/cartSlice";
import { listenToBrandsByBusinessType } from "../../../redux/commercePartnersSlice";
import {
  groceryCategories,
  grocerySubcategories,
  groceryProducts,
  groceryVariants,
} from "../../../CATEGORIES/GroceryCategories";
import {
  type AttributeItem,
  productColors,
  productColorAttributes,
  GENERAL_COLOR_ATTRIBUTE_ID,
  Units,
  StorageRequirements,
} from "../../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════
   TOKENS — unchanged, grocery's own colour language
═══════════════════════════════════════════════ */
const T = {
  bg:        "#F7F6F2",
  bgMuted:   "#EEECE6",
  bgDark:    "#14180F",
  ink:       "#14180F",
  inkMid:    "#2E3828",
  muted:     "#727868",
  mutedSoft: "#A8AC9E",
  border:    "rgba(50,60,30,0.09)",
  green:     "#264D0E",
  greenMid:  "#3D7022",
  greenLight:"#6FA845",
  greenPale: "#EEF5E8",
  saffron:   "#D97706",
  saffronPale:"#FBF0DD",
  red:       "#C0392B",
  cream:     "#FDF8EE",
  grad:    "linear-gradient(135deg,#264D0E 0%,#3D7022 45%,#D97706 100%)",
  gradH:   "linear-gradient(90deg,#264D0E 0%,#3D7022 55%,#D97706 100%)",
  farmGrad:"linear-gradient(135deg,#264D0E 0%,#D97706 50%,#C0392B 100%)",
};

/* ═══════════════════════════════════════════════
   ATTRIBUTE IDS — must match AddGrocery.tsx exactly
═══════════════════════════════════════════════ */
const SALE_UNIT_ATTRIBUTE_ID = 90201; // -> Units
const VARIANT_ATTRIBUTE_ID   = 90202; // -> groceryVariants

const fmtPrice = (n: number, sym = "€") =>
  `${sym}${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function resolveColorGroup(): AttributeItem | undefined {
  if (productColorAttributes?.values?.length) return productColorAttributes;
  return productColors.find((g) => g.id === GENERAL_COLOR_ATTRIBUTE_ID);
}

/** "3D sphere" radial-gradient look for a hex swatch — same recipe as AddGrocery. */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%)`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.30) 0%, transparent 55%)`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(",");
}

/** Customer enters how much they want in the product's own fixed sale
 *  unit (bread stays "Piece", flour stays "Kilogram", never a unit the
 *  shopper picks). This just converts that amount into how many packs
 *  to add to the cart — no unit-conversion UI, no unit switcher. */
function packsNeeded(soldByQty: number, desiredAmount: number): number {
  const qty = soldByQty || 1;
  if (!desiredAmount || desiredAmount <= 0) return 1;
  return Math.max(1, Math.ceil(desiredAmount / qty));
}

// Minimal country → continent map. Extend as needed.
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe", "czech republic": "Europe",
  hungary: "Europe", romania: "Europe", croatia: "Europe", iceland: "Europe",

  "united states": "North America", canada: "North America", mexico: "North America",

  brazil: "South America", argentina: "South America", chile: "South America",
  peru: "South America", colombia: "South America",

  nigeria: "Africa", kenya: "Africa", "south africa": "Africa", egypt: "Africa",
  morocco: "Africa", ghana: "Africa", tanzania: "Africa", ethiopia: "Africa",

  china: "Asia", japan: "Asia", india: "Asia", thailand: "Asia",
  "united arab emirates": "Asia", "south korea": "Asia", indonesia: "Asia",
  vietnam: "Asia", "saudi arabia": "Asia", israel: "Asia", turkey: "Asia",

  australia: "Oceania", "new zealand": "Oceania", fiji: "Oceania",
};

const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* ═══════════════════════════════════════════════
   LOCAL vs GLOBAL TIER DEFINITION
   tier 0 = same city
   tier 1 = same country
   tier 2 = same continent
   tier 3 = rest of world

   LOCAL  toggle → tier 0-1 only (near the user: city/country)
   GLOBAL toggle → tier 2-3 only (continent and beyond)
   The two sets never overlap, so switching the toggle never repeats
   a store/product the other mode already showed. If a mode's set is
   empty, that mode shows its own "nothing found" state instead of
   silently falling back to the other set.
═══════════════════════════════════════════════ */
const isLocalTier  = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════
   SHOP-BY-CATEGORY GROUPS
═══════════════════════════════════════════════ */
const GROCERY_CATEGORY_GROUPS = [
  {
    title: "Bakery, Dairy & Breakfast",
    accent: T.greenMid,
    ids: [150000001, 150000002, 150000003, 150000007],
  },
  {
    title: "Beverages & Snacks",
    accent: T.saffron,
    ids: [150000004, 150000005, 150000006, 150000013],
  },
  {
    title: "Cooking & Canned Goods",
    accent: T.red,
    ids: [150000008, 150000009, 150000010, 150000011],
  },
  {
    title: "Baking, Baby & Health",
    accent: T.greenLight,
    ids: [150000012, 150000014, 150000015, 150000016],
  },
  {
    title: "World Food & Deli",
    accent: T.saffron,
    ids: [150000017, 150000018, 150000019, 150000020],
  },
].map(group => ({
  ...group,
  items: group.ids.map(id => groceryCategories.find(c => c.id === id)).filter(Boolean) as typeof groceryCategories,
}));

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,600;1,400;1,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=Space+Mono:wght@400;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,input,select{font-family:inherit;cursor:pointer;}
  ::-webkit-scrollbar{display:none;}
  input::placeholder{color:rgba(255,255,255,.65);}

  @keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes mc-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes mc-pulse{0%,100%{opacity:1}50%{opacity:.35}}

  /* Header pill buttons — same anatomy as FastHome's .h-btn */
  .h-btn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
         border:1px solid rgba(255,255,255,.28);cursor:pointer;color:#fff;
         font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
         flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
  .h-btn:hover{background:rgba(255,255,255,.26);}

  /* Shop-by-category group panel */
  .scg-card{flex-shrink:0;background:#fff;border-radius:20px;overflow:hidden;
            border:1.5px solid rgba(50,60,30,.09);box-shadow:0 6px 28px rgba(38,77,14,.06);
            transition:box-shadow .22s,border-color .22s,transform .22s;}
  .scg-tile{background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden;}
  .scg-tile-inner{width:100%;aspect-ratio:1/1;border-radius:14px;overflow:hidden;position:relative;
                  background:#EEECE6;transition:transform .22s;}
  .scg-tile:hover .scg-tile-inner{transform:scale(1.03);}
  .scg-tile.sel .scg-tile-inner{outline:2.5px solid var(--sel-accent);outline-offset:2px;}

  /* Circular product cards — worldclass 3D green treatment */
  .gdish-circle{transition:transform .22s cubic-bezier(.34,1.56,.64,1);}
  .gdish-circle:hover{transform:translateY(-3px) scale(1.05);}
  .gdish-photo{position:relative;border-radius:50%;overflow:hidden;background:#EEECE6;
               box-shadow:
                 0 12px 26px rgba(38,77,14,.26),
                 0 0 0 3px rgba(111,168,69,.20),
                 0 0 0 6px rgba(61,112,34,.08),
                 inset 0 -10px 18px rgba(20,24,15,.20),
                 inset 0 7px 12px rgba(255,255,255,.40);
               transition:box-shadow .22s,transform .22s;}
  .gdish-photo::after{content:'';position:absolute;inset:0;border-radius:50%;pointer-events:none;
               background:radial-gradient(circle at 30% 24%, rgba(255,255,255,.55) 0%, rgba(255,255,255,.08) 35%, transparent 60%);}
  .gdish-circle:hover .gdish-photo{box-shadow:
                 0 16px 34px rgba(38,77,14,.34),
                 0 0 0 3px rgba(111,168,69,.32),
                 0 0 0 7px rgba(61,112,34,.12),
                 inset 0 -10px 18px rgba(20,24,15,.20),
                 inset 0 7px 12px rgba(255,255,255,.45);}
  .gdish-plus{transition:transform .15s;}
  .gdish-plus:hover{transform:scale(1.08);}
  .gdish-plus:active{transform:scale(.92);}

  /* Sidebar / sheet tree */
  .stree{background:#fff;border-radius:12px;margin-bottom:8px;overflow:hidden;
         border:1.5px solid rgba(50,60,30,.09);}
  .stree-hd{display:flex;align-items:center;justify-content:space-between;
            cursor:pointer;padding:13px 14px;user-select:none;transition:background .14s;}
  .stree-hd:hover,.stree-hd.active{background:#EEF5E8;}
  .stree-sub{padding:9px 14px 9px 30px;font-size:12px;color:#727868;
             display:flex;align-items:center;justify-content:space-between;
             border-top:1px solid rgba(50,60,30,.06);transition:background .12s;}
  .stree-sub:hover{background:#F7F6F2;color:#14180F;}
  .stree-sub.sel{background:#EEF5E8;color:#264D0E;font-weight:700;}
  .stree-sub span:first-child{cursor:pointer;flex:1;}
  .stree-prod{padding:8px 14px 8px 44px;font-size:11px;cursor:pointer;color:#A8AC9E;
              border-top:1px solid rgba(50,60,30,.04);transition:background .12s;}
  .stree-prod:hover{background:#F7F6F2;color:#14180F;}
  .stree-prod.sel{background:#EEF5E8;color:#264D0E;font-weight:700;}

  /* Add controls */
  .add-dot{width:32px;height:32px;border-radius:50%;border:none;
           background:linear-gradient(135deg,#264D0E,#3D7022,#D97706);color:#fff;
           display:flex;align-items:center;justify-content:center;font-size:20px;line-height:1;
           box-shadow:0 3px 12px rgba(61,112,34,.45);transition:transform .18s;}
  .add-dot:hover{transform:scale(1.14);}
  .qpill{display:inline-flex;align-items:center;background:rgba(61,112,34,.9);border-radius:999px;
         padding:3px 5px;border:1.5px solid rgba(255,255,255,.2);}
  .qpill button{width:24px;height:24px;border-radius:50%;border:none;background:rgba(255,255,255,.18);
                color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;}
  .qpill button:hover{background:rgba(255,255,255,.32);}

  /* Search wrap */
  .sw{display:flex;align-items:center;flex:1;background:rgba(255,255,255,.18);border-radius:999px;
      overflow:hidden;border:1.5px solid rgba(255,255,255,.32);transition:border-color .2s,box-shadow .2s;}
  .sw:focus-within{border-color:#fff;box-shadow:0 0 0 3px rgba(255,255,255,.12);}
  .sw input{flex:1;border:none;outline:none;padding:10px 14px;font-size:13px;color:#fff;background:transparent;}
  .sw-btn{width:42px;min-height:40px;background:rgba(255,255,255,.16);border:none;cursor:pointer;
          display:flex;align-items:center;justify-content:center;flex-shrink:0;border-radius:0 999px 999px 0;}

  /* Detail sheet colour swatches */
  .gd-color-cell{display:flex;flex-direction:column;align-items:center;gap:5px;cursor:pointer;background:none;border:none;padding:0;}
  .gd-color-sphere{width:34px;height:34px;border-radius:50%;flex-shrink:0;transition:transform .18s;
                    box-shadow:inset -5px -4px 10px rgba(0,0,0,.28),inset 4px 4px 8px rgba(255,255,255,.18),0 4px 14px rgba(0,0,0,.18);}
  .gd-color-cell:hover .gd-color-sphere{transform:scale(1.1) translateY(-2px);}
  .gd-color-sphere.sel{outline:2.5px solid #3D7022;outline-offset:2px;}
  .gd-color-name{font-size:9.5px;font-weight:600;color:#727868;text-align:center;max-width:56px;}
  .gd-color-cell.sel .gd-color-name{color:#264D0E;font-weight:800;}

  .gd-opt-row{transition:border-color .15s,background .15s;}
  .gd-opt-row:hover{border-color:rgba(61,112,34,.45);}

  .gd-field{width:100%;border:1.5px solid rgba(50,60,30,.14);border-radius:10px;background:#F7F6F2;
            padding:9px 12px;font-size:13px;color:#14180F;outline:none;}
  .gd-field:focus{border-color:#3D7022;}

  /* Mobile quantity stepper in the detail sheet */
  .gd-qty-stepper{display:flex;align-items:center;flex:1;border:1.5px solid rgba(50,60,30,.14);
                  border-radius:10px;background:#F7F6F2;overflow:hidden;}
  .gd-qty-stepper button{width:40px;height:42px;border:none;background:#EEF5E8;color:#3D7022;
                         display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s;}
  .gd-qty-stepper button:hover{background:#DDEBD1;}
  .gd-qty-stepper input{flex:1;min-width:0;text-align:center;border:none;outline:none;background:transparent;
                        font-size:14px;font-weight:700;color:#14180F;}
`;

/* ═══════════════════════════════════════════════
   CATEGORY TREE — leaves SELECT a filter instead of navigating
   (grocery's own logic — retained exactly)
═══════════════════════════════════════════════ */
const GroceryCategoryTree: React.FC<{
  activeCatId: number | null;
  onSelect: (id: number) => void;
  closePanel: () => void;
}> = ({ activeCatId, onSelect, closePanel }) => {
  const [openCatId, setOpenCatId] = useState<number | null>(null);
  const [openSubId, setOpenSubId] = useState<number | null>(null);

  const pick = (id: number) => {
    onSelect(id);
    closePanel();
  };

  return (
    <div>
      {groceryCategories.map(cat => {
        const isCatOpen = openCatId === cat.id;
        const subs = grocerySubcategories.filter(s => s.parentId === cat.id);
        return (
          <div key={cat.id} className="stree">
            <div className={`stree-hd${activeCatId === cat.id ? " active" : ""}`} onClick={() => setOpenCatId(isCatOpen ? null : cat.id)}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: (cat.color ?? T.greenMid) + "1A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {cat.icon && React.createElement(cat.icon as any, { size: 14, color: cat.color ?? T.greenMid })}
                </div>
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 700, color: T.ink, cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); pick(cat.id); }}>
                  {cat.name}
                </span>
              </div>
              <motion.div animate={{ rotate: isCatOpen ? 90 : 0 }} transition={{ duration: 0.16 }}>
                <ChevronRight size={13} color={T.muted} />
              </motion.div>
            </div>

            <AnimatePresence>
              {isCatOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                  {subs.map(sub => {
                    const isSubOpen = openSubId === sub.id;
                    const prods = groceryProducts.filter(p => p.parentId === sub.id);
                    return (
                      <div key={sub.id}>
                        <div className={`stree-sub${activeCatId === sub.id ? " sel" : ""}`}>
                          <span onClick={() => pick(sub.id)}>{sub.name}</span>
                          {prods.length > 0 && (
                            <motion.div animate={{ rotate: isSubOpen ? 90 : 0 }} transition={{ duration: 0.14 }} onClick={() => setOpenSubId(isSubOpen ? null : sub.id)} style={{ cursor: "pointer", padding: 4 }}>
                              <ChevronRight size={10} color={T.mutedSoft} />
                            </motion.div>
                          )}
                        </div>
                        <AnimatePresence>
                          {isSubOpen && prods.length > 0 && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.16 }} style={{ overflow: "hidden" }}>
                              {prods.map(p => (
                                <div key={p.id} className={`stree-prod${activeCatId === p.id ? " sel" : ""}`} onClick={() => pick(p.id)}>
                                  {p.name}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   GROCERY PRODUCT DETAIL — bottom sheet (mobile) / modal (desktop).
   Quantity input is FIXED to the product's own sold unit (set by the
   seller — bread stays "Piece", flour stays "Kilogram"). The shopper
   never picks a unit, only how much of that fixed unit they want.
   Desktop: plain number field. Mobile: +/- stepper around the field.
═══════════════════════════════════════════════════════════════ */
interface GroceryDetailProps {
  item: any | null;
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  getQty: (id: string) => number;
  onSetQty: (item: any, targetQty: number, photo?: string) => void;
}

const GroceryDetailView: React.FC<GroceryDetailProps> = ({ item, isOpen, onClose, isMobile, getQty, onSetQty }) => {
  const colorGroup = resolveColorGroup();
  const [selectedColorId, setSelectedColorId] = useState<number | null>(null);
  const [desiredAmount, setDesiredAmount] = useState<number>(1);

  const colorIds: number[] = item?.attributes?.[GENERAL_COLOR_ATTRIBUTE_ID] ?? [];
  const variants: Record<string, { imageUrl?: string; stock?: number }> = item?.variants ?? {};
  const variantTagIds: number[] = item?.attributes?.[VARIANT_ATTRIBUTE_ID] ?? [];
  const soldUnitId: number | null = item?.attributes?.[SALE_UNIT_ATTRIBUTE_ID] ?? null;
  const soldUnitName = soldUnitId != null ? Units.find(u => u.id === soldUnitId)?.name ?? null : null;
  const soldQty = item?.groceryDetails?.unitQuantity ?? 1;
  const details = item?.groceryDetails ?? {};
  const storageInfo = details.storageRequirement != null ? StorageRequirements.find(s => s.id === details.storageRequirement) : null;

  useEffect(() => {
    if (!item) return;
    setSelectedColorId(colorIds.length ? colorIds[0] : null);
    setDesiredAmount(soldQty || 1);
  }, [item?.listingId]);

  if (!item) return null;

  const activePhoto = (selectedColorId != null && variants[String(selectedColorId)]?.imageUrl)
    || item.photos?.[0];

  const qty = getQty(item.listingId);
  const packs = packsNeeded(soldQty, desiredAmount);
  const priceEach = item.rate ?? 0;
  const stepUnit = soldQty || 1;

  const handleSetToComputedPacks = () => onSetQty(item, packs, activePhoto);
  const step = (delta: number) => onSetQty(item, Math.max(0, qty + delta), activePhoto);

  const Content = () => (
    <>
      <div style={{ position: "relative", width: "100%", height: isMobile ? 160 : 190, background: T.cream, flexShrink: 0 }}>
        {activePhoto ? (
          <img src={activePhoto} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Package size={40} color={T.greenMid} style={{ opacity: 0.4 }} />
          </div>
        )}
        <button onClick={onClose} style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,.18)" }}>
          <X size={15} color={T.ink} />
        </button>
        {(item.isOrganic || item.isFresh) && (
          <div style={{ position: "absolute", top: 10, left: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            {item.isOrganic && <span style={{ background: T.greenMid, color: "#fff", fontSize: 9, fontWeight: 800, padding: "3px 9px", borderRadius: 999 }}>🌿 Organic</span>}
            {item.isFresh && <span style={{ background: "#2E7D32", color: "#fff", fontSize: 9, fontWeight: 800, padding: "3px 9px", borderRadius: 999 }}>🌱 Fresh</span>}
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 8px" }}>
        {details.brand && (
          <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: T.saffron, textTransform: "uppercase", marginBottom: 4 }}>{details.brand}</p>
        )}
        <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 19, color: T.ink, marginBottom: 8, lineHeight: 1.25 }}>
          {item.name}
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", background: T.gradH, backgroundSize: "200% 100%", borderRadius: 999, padding: "4px 14px", fontWeight: 900, color: "#fff", fontSize: 15 }}>
            {fmtPrice(priceEach)}
          </span>
          {soldUnitName && (
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>/ {soldQty} {soldUnitName}</span>
          )}
        </div>

        {colorIds.length > 0 && colorGroup?.values && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 8, textTransform: "uppercase" }}>Choose a colour</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {colorIds.map((cid) => {
                const c = colorGroup.values!.find(v => v.id === cid);
                if (!c) return null;
                const hex = c.hexCodes?.[0];
                const sel = selectedColorId === cid;
                return (
                  <button key={cid} type="button" className={`gd-color-cell${sel ? " sel" : ""}`} onClick={() => setSelectedColorId(cid)}>
                    <div className={`gd-color-sphere${sel ? " sel" : ""}`} style={{ background: hex ? sphere3D(hex) : sphere3D(T.bgMuted) }} />
                    <span className="gd-color-name">{c.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {variantTagIds.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 8, textTransform: "uppercase" }}>Quality / Variant</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {variantTagIds.map((vid) => {
                const v = (groceryVariants as any[]).find((g) => g.id === vid);
                if (!v) return null;
                return (
                  <span key={vid} style={{ fontSize: 11.5, fontWeight: 700, padding: "6px 12px", borderRadius: 999, background: T.greenPale, color: T.greenMid, border: `1.5px solid rgba(61,112,34,.2)` }}>
                    {v.name}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Quantity — fixed to the product's own sold unit. No unit
            picker: the shopper only ever types/steps how much of it
            they want. Desktop = plain field. Mobile = +/- stepper. */}
        

        {item.description && (
          <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 16 }}>{item.description}</p>
        )}

        {(details.packagingType || details.weightVolumeValue || storageInfo || details.countryOfOrigin || details.expirationDate) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10, padding: "12px 14px", background: T.bgMuted, borderRadius: 12 }}>
            {details.weightVolumeValue > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.inkMid }}>
                <Tag size={13} color={T.greenMid} /> {details.weightVolumeValue} {details.weightVolumeUnit}
              </div>
            )}
            {details.packagingType && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.inkMid }}>
                <Package size={13} color={T.greenMid} /> {details.packagingType}
              </div>
            )}
            {storageInfo && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.inkMid }}>
                {storageInfo.name === "Frozen" ? <Snowflake size={13} color={T.greenMid} /> : storageInfo.name === "Refrigerated" ? <Thermometer size={13} color={T.greenMid} /> : <Sun size={13} color={T.greenMid} />}
                {storageInfo.name}
              </div>
            )}
            {details.countryOfOrigin && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.inkMid }}>
                <Truck size={13} color={T.greenMid} /> Origin: {details.countryOfOrigin}
              </div>
            )}
            {details.expirationDate && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.inkMid }}>
                <Calendar size={13} color={T.greenMid} /> Best before {details.expirationDate}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: "12px 20px 18px", borderTop: `1px solid ${T.border}`, background: "#fff", flexShrink: 0 }}>
        {qty > 0 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: 50, background: "#fff", border: `2px solid ${T.greenMid}`, borderRadius: 999, padding: "0 8px" }}>
            <button onClick={() => step(-1)} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.greenMid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <Minus size={16} color="#fff" />
            </button>
            <span style={{ color: T.greenMid, fontWeight: 900, fontSize: 16 }}>{qty} in cart</span>
            <button onClick={() => step(1)} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: T.greenMid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <Plus size={16} color="#fff" />
            </button>
          </div>
        ) : (
          <button onClick={() => step(1)}
            style={{ width: "100%", height: 50, borderRadius: 999, border: "none", background: T.grad, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", boxShadow: "0 6px 20px rgba(38,77,14,.28)" }}>
            <ShoppingCart size={16} color="#fff" />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 13.5, letterSpacing: "0.03em" }}>
              ADD TO CART · {fmtPrice(priceEach)}
            </span>
          </button>
        )}
      </div>
    </>
  );

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div key="backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
            style={{ position: "fixed", inset: 0, background: "rgba(20,24,15,.5)", zIndex: 1000 }} />
          {isMobile ? (
            <motion.div key="sheet" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 32 }}
              style={{ position: "fixed", left: 0, right: 0, bottom: 0, maxHeight: "88vh", background: "#fff", borderRadius: "22px 22px 0 0", zIndex: 1001, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 -8px 40px rgba(20,24,15,.25)" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(61,112,34,.3)", margin: "10px auto 0", flexShrink: 0 }} />
              <Content />
            </motion.div>
          ) : (
            <motion.div key="modal-wrap" style={{ position: "fixed", inset: 0, zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
              <motion.div key="modal" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 340, damping: 32 }} onClick={e => e.stopPropagation()}
                style={{ width: 440, maxWidth: "100%", maxHeight: "86vh", background: "#fff", borderRadius: 22, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 30px 80px rgba(20,24,15,.32)" }}>
                <Content />
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
const GroceriesHome: React.FC = () => {
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();
  const { userId } = useParams<{ userId: string }>();

  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);
  
  const listings         = useSelector((s: RootState) => s.products.products);
const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const [isMobile,       setIsMobile]       = useState(window.innerWidth <= 768);
  const [searchQ,        setSearchQ]        = useState("");
  const [searchFocused,  setSearchFocused]  = useState(false);

  /* category filter — SELECTABLE, no navigation (grocery's own logic, retained) */
  const [activeCat,      setActiveCat]      = useState<number | null>(null);

  /* location */
  const [userCity,       setUserCity]       = useState<string | null>(null);
  const [userCountry,    setUserCountry]    = useState<string | null>(null);
  const [showGlobal,     setShowGlobal]     = useState(false);
  const [locLoading,     setLocLoading]     = useState(true);
  const [locDenied,      setLocDenied]      = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  /* category nav panels */
  const [catOpen,        setCatOpen]        = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);

  /* shop-by-category section */
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  /* product detail sheet */
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => {
  const unsub = dispatch(listenToBrandsByBusinessType(1) as any);
  return () => { if (typeof unsub === "function") unsub(); };
}, [dispatch]);

  /* geolocation */
  useEffect(() => {
    if (!navigator.geolocation) { setLocDenied(true); setLocLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocLoading(false);
        if ((window as any).google?.maps) {
          new (window as any).google.maps.Geocoder().geocode(
            { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
            (res: any, st: string) => {
              if (st === "OK" && res?.[0]) {
                const comps = res[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                setUserCountry(get("country"));
              }
            }
          );
        }
      },
      () => { setLocDenied(true); setLocLoading(false); }
    );
  }, []);


  useEffect(() => { 
  dispatch(filterLocalProductsByCountry(null) as any); 
}, [dispatch]);

useEffect(() => {
  if (!userCountry) return;
  dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
}, [userCountry, showGlobal, dispatch]);
 
  const locationActive = !locDenied && !locLoading && userCountry !== null;

  const partners = useMemo(() => {
    return (commercePartners || []).filter((p: any) => p.categoryId === 1);
  }, [commercePartners]);

  /* only stores that actually have at least one published product —
     used everywhere a store is shown or counted (Trending row) */
  const partnersWithProducts = useMemo(() => {
    const brandIdsWithProducts = new Set(listings.map((l: any) => l.brandId || l.hostId));
    return partners.filter((p: any) => brandIdsWithProducts.has(p.brandId));
  }, [partners, listings]);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    partners.forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [partners]);

  /* tier 0 = same city, 1 = same country, 2 = same continent, 3 = rest of world */
  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    partners.forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [partners, userCity, userCountry]);

  /* ── stores ranked by proximity, used by the Trending row.
     LOCAL  mode → tier 0-1 only (same city/country as the shopper).
     GLOBAL mode → tier 2-3 only (same continent and beyond).
     The two sets are disjoint — toggling never repeats a store the
     other mode already showed, and each mode has its own empty state
     instead of silently falling back to the other set. ── */
  const trendingStores = useMemo(() => {
    let list = [...partnersWithProducts];
    if (locationActive) {
      list = list.filter((p: any) => {
        const tier = brandTier.get(p.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    list.sort((a: any, b: any) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return list.slice(0, 16);
  }, [partnersWithProducts, brandTier, showGlobal, locationActive]);

  const handleToggleGlobal = () => setShowGlobal(v => !v);

  /* ── category select (toggle) — used by both Shop-by-Category tiles
     and the category tree/sheet. Retained grocery logic: this FILTERS
     the product list below, it never navigates away. ── */
  const handleCatSelect = (id: number) => {
    setActiveCat(prev => (prev === id ? null : id));
  };
  const activeCatName = useMemo(() => {
    if (activeCat == null) return null;
    return groceryCategories.find(c => c.id === activeCat)?.name
      ?? grocerySubcategories.find(s => s.id === activeCat)?.name
      ?? groceryProducts.find(p => p.id === activeCat)?.name
      ?? null;
  }, [activeCat]);

  /* ── products (category + search), grouped by store below.
     A category picked in Shop-by-Category filters every store's
     products down to that category — global/local toggle then just
     controls which stores/order they're grouped in. ── */
  const brandProducts = useMemo(() => {
  let list = filteredListings ?? listings;

    if (activeCat != null) {
      list = list.filter((l: any) =>
        l.categoryId === activeCat || l.subcategoryId === activeCat || l.productId === activeCat
      );
    }
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      list = list.filter((l: any) =>
        (l.name ?? "").toLowerCase().includes(q) || (l.description ?? "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCat, listings, searchQ, filteredListings]);

  /* ── same LOCAL (tier 0-1) / GLOBAL (tier 2-3) split as the stores
     row, applied per brand group so the product grid and the
     Trending Stores row always agree on what "local" and "global"
     mean. ── */
  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    brandProducts.forEach((item: any) => {
      const bId = item.brandId || item.hostId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map(bId => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter(g => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    groups = [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return groups;
  }, [brandProducts, showGlobal, brandTier, locationActive]);

  const getQty  = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const addItem = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(addToCart({ listingId: item.listingId, name: item.name ?? "", rate: item.rate ?? 0, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };
  const decItem = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(decrementCartItem({ listingId: item.listingId }));
  };

  /** Used by the detail sheet's quantity picker. Converges the cart
   *  to `targetQty` by calling the exact same addToCart /
   *  decrementCartItem actions used everywhere else — no new redux
   *  shape, no change to checkout. */
  const setItemQty = (item: any, targetQty: number, photo?: string) => {
    const current = getQty(item.listingId);
    const diff = targetQty - current;
    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        dispatch(addToCart({ listingId: item.listingId, name: item.name ?? "", rate: item.rate ?? 0, photo: photo ?? item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
      }
    } else if (diff < 0) {
      for (let i = 0; i < -diff; i++) {
        dispatch(decrementCartItem({ listingId: item.listingId }));
      }
    }
  };

  const goToCheckout = () => navigate(`/productcheckout/${userId}`);
  const goToBrand    = (id: string) => navigate(`/storehome/${id}`);
  const openDetails  = (item: any) => setSelectedItem(item);

  /* ══════════════════════════
     HEADER — same anatomy/order/logic as FastHome's HeaderRow:
     location pill → search → categories → cart. Grocery colours kept.
  ══════════════════════════ */
  const HeaderBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      {locLoading ? (
        <div className="h-btn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "mc-pulse 1.2s infinite" }} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Locating…</span>
        </div>
      ) : locDenied ? (
        <button className="h-btn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : (
        <button onClick={handleToggleGlobal} className="h-btn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div className="sw" style={{ borderColor: searchFocused ? "#fff" : "rgba(255,255,255,.32)" }}>
        <input
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search stores or products…"
        />
        {searchQ && (
          <button onClick={() => setSearchQ("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button className="sw-btn"><Search size={16} color="#fff" /></button>
      </div>

      <button
        onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v))}
        className="h-btn"
      >
        <LayoutGrid size={14} />
        <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="h-btn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: T.red, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  /* ══════════════════════════
     LOCATION MODAL — same pattern as FastHome, grocery colours
  ══════════════════════════ */
  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(20,24,15,.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(38,77,14,.3)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: T.gradH, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, marginBottom: 8, color: T.ink }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see nearby stores.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: T.gradH, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "'Syne',sans-serif" }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ══════════════════════════
     MOBILE CATEGORY BOTTOM SHEET
  ══════════════════════════ */
  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(20,24,15,.55)", zIndex: 400 }} />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: `1.5px solid ${T.border}`, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: T.bgMuted, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.greenMid }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>
              <GroceryCategoryTree activeCatId={activeCat} onSelect={handleCatSelect} closePanel={closePanel} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ══════════════════════════
     TRENDING STORES — shows STORE logo + name, with a Local/Global
     toggle mirroring the products toggle. LOCAL = tier 0-1 (city/
     country), GLOBAL = tier 2-3 (continent+). Each mode shows its own
     "nothing found" message rather than falling back to the other.
     Clicking a store navigates straight to that store via brandId.
  ══════════════════════════ */
  const TrendingStoresBar = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: T.gradH, borderRadius: 18, boxShadow: "0 8px 24px rgba(38,77,14,.18)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StoreIcon size={14} color="#fff" />
          <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 12, color: "#fff", letterSpacing: "0.04em" }}>Trending right now</span>
        </div>
        {!locLoading && !locDenied && (
          <button onClick={handleToggleGlobal}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.35)", borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", flexShrink: 0 }}>
            {showGlobal ? <Globe size={12} /> : <MapPin size={12} />}
            {showGlobal ? "Global" : "Local"}
          </button>
        )}
      </div>
      {trendingStores.length === 0 ? (
        <p style={{ color: "rgba(255,255,255,.85)", fontSize: 12, fontWeight: 600 }}>
          {showGlobal ? "No global stores found." : "No local stores found."}
        </p>
      ) : (
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {trendingStores.map((partner: any) => {
            const init = (partner.name ?? "S").split(" ").slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? "").join("");
            return (
              <button key={partner.brandId} className="gdish-circle"
                onClick={() => goToBrand(partner.brandId)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
                <div className="gdish-photo" style={{
                  width: isMobile ? 58 : 68, height: isMobile ? 58 : 68,
                  border: "2px solid rgba(255,255,255,.5)", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {partner.image ? (
                    <img src={partner.image} alt={partner.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: T.greenMid, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>{init}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", maxWidth: 74, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {partner.name || "Store"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  /* ══════════════════════════
     SHOP BY CATEGORY — same header UI/collapse logic as FastHome's
     ShopByCategory, but the grouping content and select-to-filter
     behaviour stay grocery's own (retained, no navigation).
  ══════════════════════════ */
  const ShopByCategory = () => (
    <div style={{ marginBottom: 28 }}>
      <div
        onClick={() => setShopCatExpanded(v => !v)}
        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer", userSelect: "none" }}
      >
        <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: isMobile ? 16 : 19, color: T.ink, letterSpacing: -0.3 }}>
          🛒 Shop by Category
        </span>
        <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={20} color={T.muted} />
        </motion.div>
      </div>

      <AnimatePresence initial={false}>
        {shopCatExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}
          >
            <div style={isMobile
              ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, paddingBottom: 6 }
              : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
            }>
              {GROCERY_CATEGORY_GROUPS.map(group => (
                <div
                  key={group.title}
                  className="scg-card"
                  style={{ width: isMobile ? "78vw" : "auto", scrollSnapAlign: isMobile ? "start" : undefined }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow = `0 8px 0 ${group.accent}22, 0 16px 38px rgba(20,24,15,.16)`;
                    el.style.borderColor = group.accent;
                    el.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow = "0 6px 28px rgba(38,77,14,.06)";
                    el.style.borderColor = "rgba(50,60,30,.09)";
                    el.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: isMobile ? "12px 12px 6px" : "15px 16px 8px" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: group.accent, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: isMobile ? 12 : 14, color: T.ink, letterSpacing: -0.2 }}>
                      {group.title}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 6 : 8, padding: isMobile ? "0 10px 10px" : "0 12px 12px" }}>
                    {group.items.map((cat: any) => {
                      const Icon = cat.icon;
                      const sel = activeCat === cat.id;
                      return (
                        <button
                          key={cat.id}
                          className={`scg-tile${sel ? " sel" : ""}`}
                          style={{ "--sel-accent": group.accent } as React.CSSProperties}
                          onClick={() => handleCatSelect(cat.id)}
                        >
                          <div className="scg-tile-inner">
                            {cat.image ? (
                              <>
                                <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${T.bgDark}EE, transparent 55%)` }} />
                                <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)", padding: "0 4px" }}>
                                  {cat.name}
                                </span>
                                {sel && (
                                  <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: group.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Check size={11} color="#fff" strokeWidth={3} />
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, background: `${group.accent}0F`, position: "relative" }}>
                                {Icon && <Icon size={isMobile ? 22 : 26} color={group.accent} />}
                                <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.ink, textAlign: "center", padding: "0 4px", lineHeight: 1.2 }}>
                                  {cat.name}
                                </span>
                                {sel && (
                                  <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: group.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Check size={11} color="#fff" strokeWidth={3} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <button onClick={() => handleCatSelect(group.items[0].id)}
                    style={{ width: "100%", padding: isMobile ? "8px 12px" : "10px 16px", background: "none", border: "none", borderTop: "1.5px solid rgba(50,60,30,.06)", cursor: "pointer", fontSize: isMobile ? 11 : 12.5, fontWeight: 700, color: group.accent, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    See all <ArrowRight size={isMobile ? 12 : 14} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  /* ══════════════════════════
     BRAND ROW HEADER — clicking navigates to the store via brandId
  ══════════════════════════ */
  const BrandRowHeader: React.FC<{ brandId: string }> = ({ brandId }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Store";
    const shopImg  = partner?.image;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} style={{ background: "none", cursor: "pointer", padding: 0, width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
          {shopImg
            ? <img src={shopImg} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", background: T.greenMid, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{shopName[0]?.toUpperCase()}</span></div>}
        </button>
        <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 13, color: T.ink, textDecoration: "underline", textDecorationColor: T.saffron, textDecorationThickness: 2, textUnderlineOffset: 4 }}>
          {shopName}
        </span>
        <div style={{ flex: 1 }} />
        <motion.button
          whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
          onClick={() => goToBrand(brandId)}
          style={{ width: 28, height: 28, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(0,0,0,.13)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={15} color={T.greenMid} />
        </motion.button>
      </div>
    );
  };

  /* ══════════════════════════
     CIRCULAR PRODUCT CARD — worldclass 3D green photo (layered
     shadow + glassy highlight, defined by .gdish-photo in CSS above).
     List view intentionally shows PRICE ONLY — no weight/unit text.
     Unit/weight/pack size only appears once the shopper opens the
     detail sheet.
  ══════════════════════════ */
  const CircularGroceryCard: React.FC<{ item: any; isMob: boolean }> = ({ item, isMob }) => {
    const hasColorVariants = (item.attributes?.[GENERAL_COLOR_ATTRIBUTE_ID]?.length ?? 0) > 0;
    const qty = hasColorVariants ? 0 : getQty(item.listingId);
    const size = isMob ? 110 : 140;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
          <button onClick={() => openDetails(item)} className="gdish-circle"
            style={{ width: "100%", height: "100%", border: "none", cursor: "pointer", padding: 0, background: "none" }}>
            <div className="gdish-photo" style={{ width: "100%", height: "100%" }}>
              {item.photos?.[0] ? (
                <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Package size={isMob ? 26 : 34} color={T.greenMid} style={{ opacity: 0.4 }} />
                </div>
              )}
            </div>
          </button>

          <motion.button className="gdish-plus" whileTap={{ scale: 0.92 }}
            onClick={e => {
              e.stopPropagation();
              if (hasColorVariants) { openDetails(item); return; }
              if (qty === 0) addItem(item, e); else openDetails(item);
            }}
            style={{ position: "absolute", bottom: -6, right: -6, width: isMob ? 36 : 44, height: isMob ? 36 : 44, borderRadius: "50%", border: "none", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(20,24,15,.22)", zIndex: 10 }}>
            <Plus size={isMob ? 18 : 22} color={T.greenMid} />
          </motion.button>

          {item.isOrganic && (
            <div style={{ position: "absolute", top: 4, left: 4, width: 20, height: 20, borderRadius: "50%", background: T.greenMid, border: "1.5px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}>
              <Leaf size={10} color="#fff" />
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", width: "100%", minWidth: 0 }}>
          <div style={{ fontSize: isMob ? 11 : 12, fontWeight: 700, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 2, fontFamily: "'Syne',sans-serif" }}>
            {item.name}
          </div>
          <div style={{ fontSize: isMob ? 10 : 11, fontWeight: 900, color: T.greenMid, background: T.greenPale, padding: "2px 6px", borderRadius: 999, display: "inline-block" }}>
            {fmtPrice(item.rate ?? 0)}
          </div>
        </div>
      </div>
    );
  };

  /* ════════════════════════════════
     RENDER
  ════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{CSS}</style>

      <header style={{ background: T.gradH, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: "0 4px 24px rgba(38,77,14,.25)" }}>
        <HeaderBar />
      </header>

      <MobileCategorySheet />
      <LocationModal />

      <div style={{ maxWidth: 1300, margin: "0 auto", padding: isMobile ? "0" : "0 24px" }}>

        <div style={{ padding: isMobile ? "16px 14px 0" : "24px 0 0" }}>
          <TrendingStoresBar />
          <ShopByCategory />
        </div>

        <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: desktopCatOpen ? "280px 1fr" : "1fr", gap: 20, alignItems: "start", padding: isMobile ? "0 14px" : "0" }}>

          {!isMobile && desktopCatOpen && (
            <aside>
              <div style={{ position: "sticky", top: 90, maxHeight: "calc(100vh - 110px)", overflowY: "auto", background: "#fff", borderRadius: 16, padding: 14, border: `1.5px solid ${T.border}`, boxShadow: "0 4px 24px rgba(38,77,14,.06)" }}>
                <div style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".2em", color: T.muted, textTransform: "uppercase" as const, marginBottom: 2 }}>Filter by</p>
                  <p style={{ fontFamily: "'Lora',serif", fontSize: 17, fontWeight: 600, color: T.ink }}>Product Type</p>
                </div>
                <GroceryCategoryTree activeCatId={activeCat} onSelect={handleCatSelect} closePanel={closePanel} />
              </div>
            </aside>
          )}

          <main style={{ minWidth: 0 }}>

            {/* active category banner */}
            {activeCat != null && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: T.saffronPale, borderRadius: 14, border: `1.5px solid ${T.saffron}` }}>
                <Tag size={16} color={T.saffron} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.saffron, flex: 1 }}>Browsing: {activeCatName}</span>
                <button onClick={() => setActiveCat(null)} style={{ background: "#fff", border: "none", borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={12} color={T.saffron} />
                </button>
              </motion.div>
            )}

            {/* products — grouped by store, each row shows only that store's matching items */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 4, height: 20, borderRadius: 2, background: T.farmGrad }} />
                  <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? 15 : 19, fontWeight: 700, color: T.ink }}>
                    {activeCatName ?? (showGlobal ? "Global" : userCity ? `Near ${userCity}` : "Fresh Picks")}
                  </h2>
                  <span style={{ fontSize: 11, color: T.muted, background: T.bgMuted, padding: "2px 10px", borderRadius: 999 }}>{brandProducts.length}</span>
                </div>
                {locationActive && (
                  <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? T.bg : T.greenPale, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? T.border : "rgba(61,112,34,.3)"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.muted : T.greenMid, cursor: "pointer" }}>
                    {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
                  </button>
                )}
              </div>

              {productsByBrand.length === 0 ? (
                <div style={{ textAlign: "center", padding: "52px 20px" }}>
                  <div style={{ fontSize: 46, marginBottom: 12 }}>🛒</div>
                  <p style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, color: T.ink, marginBottom: 6 }}>
                    {activeCat != null
                      ? `No products found in "${activeCatName}"`
                      : locationActive
                        ? (showGlobal ? "No global products found." : "No local products found.")
                        : "No products found"}
                  </p>
                  <p style={{ fontSize: 12, color: T.muted }}>
                    {activeCat != null ? "Try a different category or check back soon." : "Products appear once stores go live."}
                  </p>
                </div>
              ) : (
                productsByBrand.map(group => (
                  <div key={group.brandId} style={{ marginBottom: 8 }}>
                    <BrandRowHeader brandId={group.brandId} />
                    <div style={{ display: "flex", gap: isMobile ? 12 : 16, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 8 }}>
                      {group.items.slice(0, 32).map((item: any) => (
                        <CircularGroceryCard key={item.listingId} item={item} isMob={isMobile} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </main>
        </div>
      </div>

      <GroceryDetailView
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        isMobile={isMobile}
        getQty={getQty}
        onSetQty={setItemQty}
      />
    </div>
  );
};

export default GroceriesHome;